import { create } from 'zustand'

// Usar la URL de producción de Cloudflare Workers
const API_BASE = import.meta.env.PROD 
  ? 'https://uss-kine.dtroncoso.workers.dev/api' 
  : '/api'

const getAuthHeaders = () => {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }
}

export const useImproveCycleStore = create((set, get) => ({
  // State
  proyectos: [],
  proyectoActual: null,
  cicloActual: null,
  loading: false,
  error: null,

  // Actions - Proyectos
  fetchProyectosByCriterio: async (criterioId) => {
    set({ loading: true, error: null })
    try {
      const response = await fetch(`${API_BASE}/proyectos-mejora/by-criterio/${criterioId}`, {
        headers: getAuthHeaders()
      })
      if (!response.ok) throw new Error('Error al cargar proyectos')
      const data = await response.json()
      set({ proyectos: data.proyectos, loading: false })
      return data.proyectos
    } catch (error) {
      set({ error: error.message, loading: false })
      throw error
    }
  },

  fetchProyecto: async (proyectoId) => {
    set({ loading: true, error: null })
    try {
      const response = await fetch(`${API_BASE}/proyectos-mejora/${proyectoId}`, {
        headers: getAuthHeaders()
      })
      if (!response.ok) throw new Error('Error al cargar proyecto')
      const data = await response.json()
      set({ proyectoActual: data.proyecto, loading: false })
      return data.proyecto
    } catch (error) {
      set({ error: error.message, loading: false })
      throw error
    }
  },

  createProyecto: async (proyectoData) => {
    set({ loading: true, error: null })
    try {
      const response = await fetch(`${API_BASE}/proyectos-mejora/create`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(proyectoData)
      })
      if (!response.ok) throw new Error('Error al crear proyecto')
      const data = await response.json()
      set({ proyectoActual: data.proyecto, cicloActual: data.ciclo, loading: false })
      return data
    } catch (error) {
      set({ error: error.message, loading: false })
      throw error
    }
  },

  // Actions - Ciclos
  fetchCiclo: async (cicloId) => {
    set({ loading: true, error: null })
    try {
      const response = await fetch(`${API_BASE}/ciclos/${cicloId}`, {
        headers: getAuthHeaders()
      })
      if (!response.ok) throw new Error('Error al cargar ciclo')
      const data = await response.json()
      set({ cicloActual: data.ciclo, loading: false })
      return data.ciclo
    } catch (error) {
      set({ error: error.message, loading: false })
      throw error
    }
  },

  fetchEstadoPestanas: async (cicloId) => {
    try {
      const response = await fetch(`${API_BASE}/ciclos/${cicloId}/estado-pestanas`, {
        headers: getAuthHeaders()
      })
      if (!response.ok) throw new Error('Error al cargar estado de pestañas')
      const data = await response.json()
      
      // Update cicloActual with estado_pestanas
      const cicloActual = get().cicloActual
      if (cicloActual && cicloActual.id === cicloId) {
        set({ 
          cicloActual: { 
            ...cicloActual, 
            estado_pestanas: data.estado_pestanas 
          } 
        })
      }
      
      return data.estado_pestanas
    } catch (error) {
      console.error('Error fetching estado pestañas:', error)
      throw error
    }
  },

  // Actions - Fase Planificar
  savePlanificar: async (cicloId, faseData) => {
    set({ loading: true, error: null })
    try {
      const response = await fetch(`${API_BASE}/fases/planificar/save`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ciclo_id: cicloId, ...faseData })
      })
      if (!response.ok) throw new Error('Error al guardar planificación')
      const data = await response.json()
      
      // Update cicloActual
      const cicloActual = get().cicloActual
      if (cicloActual) {
        set({
          cicloActual: {
            ...cicloActual,
            fases: {
              ...cicloActual.fases,
              planificar: data.fase
            }
          },
          loading: false
        })
      }
      
      return data
    } catch (error) {
      set({ error: error.message, loading: false })
      throw error
    }
  },

  completarPlanificar: async (cicloId) => {
    set({ loading: true, error: null })
    try {
      const response = await fetch(`${API_BASE}/fases/planificar/completar`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ciclo_id: cicloId })
      })
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al completar planificación')
      }
      const data = await response.json()
      
      // Refresh ciclo
      await get().fetchCiclo(cicloId)
      set({ loading: false })
      
      return data
    } catch (error) {
      set({ error: error.message, loading: false })
      throw error
    }
  },

  // Actions - Fase Hacer
  updateTareas: async (cicloId, tareas) => {
    set({ loading: true, error: null })
    try {
      const response = await fetch(`${API_BASE}/fases/hacer/update-tareas`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ciclo_id: cicloId, tareas })
      })
      if (!response.ok) throw new Error('Error al actualizar tareas')
      const data = await response.json()
      
      // Update cicloActual
      const cicloActual = get().cicloActual
      if (cicloActual) {
        set({
          cicloActual: {
            ...cicloActual,
            fases: {
              ...cicloActual.fases,
              hacer: data.fase
            }
          },
          loading: false
        })
      }
      
      return data
    } catch (error) {
      set({ error: error.message, loading: false })
      throw error
    }
  },

  saveHacer: async (cicloId, faseData) => {
    set({ loading: true, error: null })
    try {
      const response = await fetch(`${API_BASE}/fases/hacer/save`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ciclo_id: cicloId, ...faseData })
      })
      if (!response.ok) throw new Error('Error al guardar fase hacer')
      const data = await response.json()
      
      // Update cicloActual
      const cicloActual = get().cicloActual
      if (cicloActual) {
        set({
          cicloActual: {
            ...cicloActual,
            fases: {
              ...cicloActual.fases,
              hacer: data.fase
            }
          },
          loading: false
        })
      }
      
      return data
    } catch (error) {
      set({ error: error.message, loading: false })
      throw error
    }
  },

  completarHacer: async (cicloId) => {
    set({ loading: true, error: null })
    try {
      const response = await fetch(`${API_BASE}/fases/hacer/completar`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ciclo_id: cicloId })
      })
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al completar fase hacer')
      }
      const data = await response.json()
      
      // Refresh ciclo
      await get().fetchCiclo(cicloId)
      set({ loading: false })
      
      return data
    } catch (error) {
      set({ error: error.message, loading: false })
      throw error
    }
  },

  // Actions - Fase Verificar
  fetchDashboardVerificar: async (cicloId) => {
    try {
      const response = await fetch(`${API_BASE}/fases/verificar/dashboard/${cicloId}`, {
        headers: getAuthHeaders()
      })
      if (!response.ok) throw new Error('Error al cargar dashboard')
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error fetching dashboard:', error)
      throw error
    }
  },

  saveVerificar: async (cicloId, faseData) => {
    set({ loading: true, error: null })
    try {
      const response = await fetch(`${API_BASE}/fases/verificar/save`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ciclo_id: cicloId, ...faseData })
      })
      if (!response.ok) throw new Error('Error al guardar verificación')
      const data = await response.json()
      
      // Update cicloActual
      const cicloActual = get().cicloActual
      if (cicloActual) {
        set({
          cicloActual: {
            ...cicloActual,
            fases: {
              ...cicloActual.fases,
              verificar: data.fase
            }
          },
          loading: false
        })
      }
      
      return data
    } catch (error) {
      set({ error: error.message, loading: false })
      throw error
    }
  },

  completarVerificar: async (cicloId) => {
    set({ loading: true, error: null })
    try {
      const response = await fetch(`${API_BASE}/fases/verificar/completar`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ciclo_id: cicloId })
      })
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al completar verificación')
      }
      const data = await response.json()
      
      // Refresh ciclo
      await get().fetchCiclo(cicloId)
      set({ loading: false })
      
      return data
    } catch (error) {
      set({ error: error.message, loading: false })
      throw error
    }
  },

  // Actions - Fase Actuar
  estandarizar: async (cicloId, nombrePOE, solicitarAprobacion) => {
    set({ loading: true, error: null })
    try {
      const response = await fetch(`${API_BASE}/fases/actuar/estandarizar`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          ciclo_id: cicloId, 
          nombre_poe: nombrePOE,
          solicitar_aprobacion: solicitarAprobacion
        })
      })
      if (!response.ok) throw new Error('Error al estandarizar')
      const data = await response.json()
      
      // Refresh ciclo
      await get().fetchCiclo(cicloId)
      set({ loading: false })
      
      return data
    } catch (error) {
      set({ error: error.message, loading: false })
      throw error
    }
  },

  rePlanificar: async (cicloId, motivoRelanzamiento) => {
    set({ loading: true, error: null })
    try {
      const response = await fetch(`${API_BASE}/fases/actuar/re-planificar`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          ciclo_id: cicloId, 
          motivo_relanzamiento: motivoRelanzamiento
        })
      })
      if (!response.ok) throw new Error('Error al re-planificar')
      const data = await response.json()
      
      set({ cicloActual: data.nuevo_ciclo, loading: false })
      
      return data
    } catch (error) {
      set({ error: error.message, loading: false })
      throw error
    }
  },

  // Utility
  clearError: () => set({ error: null }),
  reset: () => set({ proyectos: [], proyectoActual: null, cicloActual: null, loading: false, error: null })
}))
